<template>
  <!-- removing 'theming classes' will cause style issue throughout the site -->
  <main ref="mainWrap" class="tw-w-full">
    <modio-toast />
    <div
      ref="container"
      class="tw-flex tw-flex-col tw-size-full tw-relative tw-text-theme"
      :style="{ minHeight: `${MIN_PAGE_HEIGHT}px` }"
    >
      <router-view
        v-if="!requireAccess"
        :key="$route.path"
        v-slot="{ Component }"
      >
        <component :is="Component"></component>
      </router-view>
    </div>
    <global-modals />
  </main>
</template>

<script>
import { activationStore, localModStore, authStore, gameStore } from '@stores'
import { clearGlobalModal, setGlobalModal } from '@composables/useModal.js'
import { sdkPlatform, udtParam, GAME_ID, HEADERS } from '@config/game.js'
import { onMounted, nextTick, computed, watch, ref } from 'vue'
import { updateLocalStorage, udtLoginRequest } from '@services'
import ViewContainer from '@components/View/ViewContainer.vue'
import ModioToast from '@components/Message/ModioToast.vue'
import GlobalModals from '@views/layouts/GlobalModals.vue'
import { STATUS_NOT_ACCEPTED } from '@config/options.js'
import * as sdkInterface from '@plugins/sdkInterface.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import i18n from '@plugins/localisation.js'
import { isArray } from '@helpers/utils.js'
import { MIN_PAGE_HEIGHT } from '@config'
import {
  useWindowEvents,
  useBoundaries,
  useTheming,
  useAsync,
  useSSO,
} from '@composables'
import {
  containerHeight,
  windowHeight,
  sendMessage,
} from '@composables/useWindowEvents.js'

export default {
  components: {
    ViewContainer,
    GlobalModals,
    ModioToast,
  },
  setup() {
    const { watchTheme } = useTheming()
    const { setBoundaryVars } = useBoundaries()
    const container = ref(null)
    const mainWrap = ref(null)
    const { accessTokens, isLoggedIn } = authStore()
    const { activations, getKey } = activationStore()
    const { setLocalMods } = localModStore()
    const { getSSOInfo } = useSSO()

    const { getPublicGame } = gameStore()
    const game = getPublicGame()

    const { run: runUDT } = useAsync((udt) => udtLoginRequest(udt))

    const requireAccess = computed(
      () => game.value?.status === STATUS_NOT_ACCEPTED && !isLoggedIn.value
    )

    // watch for route changes to
    // set correct theming options
    watchTheme()

    useWindowEvents()

    watch(
      requireAccess,
      (req) => {
        getSSOInfo(game.value)
        if (req) {
          setGlobalModal({ modal: MODAL_LOGIN, data: { noAccess: true } })
        }
      },
      { immediate: true }
    )

    onMounted(async () => {
      if (location.pathname !== '/') {
        location.pathname = '/'
        return
      }

      _setWrapVars()
      resizeContainer()

      // Fetch language first so correct headers are sent
      await fetchLanguageFromSdk()

      fetchTokenFromSdk()
      fetchPortalFromSdk()
      fetchPlatformFromSdk()
      fetchLocalModsFromSdk()
      setActivations()
      setLocalActivations()

      if (accessTokens.value[GAME_ID] && !isLoggedIn.value) {
        updateLocalStorage()
      } else if (udtParam) {
        clearGlobalModal()

        await runUDT(udtParam)

        if (!isLoggedIn.value && requireAccess.value) {
          setGlobalModal({ modal: MODAL_LOGIN, data: { noAccess: true } })
        }
      }
    })

    async function fetchTokenFromSdk() {
      const udt = await sdkInterface.getAuthToken()

      if (udt) {
        runUDT(udt)
      }
    }

    async function fetchLocalModsFromSdk() {
      const localMods = await sdkInterface.getLocalMods()
      if (localMods) {
        setLocalMods(localMods)
      }
    }

    async function fetchPortalFromSdk() {
      const portal = await sdkInterface.getPortal()
      if (portal) {
        HEADERS.portal = portal
      }
    }

    async function fetchPlatformFromSdk() {
      const platform = await sdkInterface.getPlatform()
      if (platform) {
        sdkPlatform.value = true
        HEADERS.platform = platform
        getPublicGame(true)
      }
    }

    async function fetchLanguageFromSdk() {
      const lang = await sdkInterface.getLanguage()
      if (lang) i18n.global.locale.value = lang
    }

    async function setActivations() {
      const ids = activations.value?.[getKey(false)]
      if (isArray(ids)) sdkInterface.setActivations(ids)
    }

    async function setLocalActivations() {
      const ids = activations.value?.[getKey(true)]
      if (isArray(ids)) sdkInterface.setLocalActivations(ids)
    }

    function onResize() {
      if (window.self === window.top) windowHeight.value = window.innerHeight

      if (!container.value) return

      containerHeight.value = container.value.scrollHeight
      sendMessage({
        height: container.value.scrollHeight,
        modioHeight: container.value.scrollHeight,
      })
    }

    function resizeContainer() {
      new ResizeObserver(onResize).observe(container.value)
      setTimeout(onResize, 1000)
    }

    function _setWrapVars() {
      // wait for DOM to refresh for mainWrap to be set
      nextTick(() => {
        setBoundaryVars('main', mainWrap.value.getBoundingClientRect())
      })
    }

    return {
      MIN_PAGE_HEIGHT,
      requireAccess,
      container,
      mainWrap,
    }
  },
}
</script>
